import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Link2, RefreshCw, Plus, ExternalLink, Phone, Mail, MapPin, Package, Edit2, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminPartenairesConfig() {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nom: '', categorie: '', description: '', telephone: '', email: '', site_web: '', zone: '', services: '', actif: true });

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/prestataires-recommandes`, { headers: H });
      const data = await res.json();
      setPartenaires(data.prestataires || data || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    try {
      const url = editId
        ? `${BACKEND}/api/admin/prestataires/${editId}`
        : `${BACKEND}/api/admin/prestataires`;
      const method = editId ? 'PUT' : 'POST';
      const body = { ...form, services: form.services.split(',').map(s => s.trim()).filter(Boolean) };
      const res = await fetch(url, { method, headers: H, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(editId ? 'Partenaire mis à jour' : 'Partenaire ajouté');
      setShowForm(false);
      setEditId(null);
      setForm({ nom: '', categorie: '', description: '', telephone: '', email: '', site_web: '', zone: '', services: '', actif: true });
      load();
    } catch { toast.error('Erreur sauvegarde'); }
  }

  async function toggleActif(id, current) {
    try {
      await fetch(`${BACKEND}/api/admin/prestataires/${id}`, {
        method: 'PUT', headers: H,
        body: JSON.stringify({ actif: !current })
      });
      load();
    } catch { toast.error('Erreur'); }
  }

  function startEdit(p) {
    setForm({
      nom: p.nom || '',
      categorie: p.categorie || '',
      description: p.description || '',
      telephone: p.telephone || '',
      email: p.email || '',
      site_web: p.site_web || '',
      zone: p.zone || '',
      services: Array.isArray(p.services) ? p.services.join(', ') : (p.services || ''),
      actif: p.actif !== false,
    });
    setEditId(p.id);
    setShowForm(true);
  }

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Prestataires & Partenaires</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Annuaire des prestataires recommandés</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }} data-testid="admin-partenaires">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-blue)' }}>Prestataires & Partenaires</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Gérez les prestataires recommandés à vos clients</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <Button onClick={() => { setShowForm(true); setEditId(null); setForm({ nom: '', categorie: '', description: '', telephone: '', email: '', site_web: '', zone: '', services: '', actif: true }); }}
            size="sm" style={{ background: 'rgba(125,177,255,0.15)', color: 'var(--admin-blue)', border: '1px solid rgba(125,177,255,0.3)' }}>
            <Plus size={14} className="mr-1" /> Ajouter un prestataire
          </Button>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="p-5 rounded-2xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(125,177,255,0.2)' }}>
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-sm" style={{ color: 'var(--admin-blue)' }}>{editId ? 'Modifier' : 'Ajouter'} un prestataire</h2>
            <button onClick={() => setShowForm(false)} style={{ color: 'var(--admin-muted)' }}><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { key: 'nom', label: 'Nom *', placeholder: 'DJ Events 46...' },
              { key: 'categorie', label: 'Catégorie', placeholder: 'DJ, Fleuriste, Traiteur...' },
              { key: 'telephone', label: 'Téléphone', placeholder: '06 XX XX XX XX' },
              { key: 'email', label: 'Email', placeholder: 'contact@prestataire.fr' },
              { key: 'site_web', label: 'Site web', placeholder: 'https://...' },
              { key: 'zone', label: 'Zone', placeholder: 'Lot, Aveyron...' },
            ].map(field => (
              <div key={field.key}>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>{field.label}</label>
                <Input placeholder={field.placeholder} value={form[field.key]}
                  onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description</label>
            <textarea placeholder="Présentation du prestataire..." value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              rows={2} className="w-full text-sm px-3 py-2 rounded-lg"
              style={{ background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none', resize: 'vertical' }} />
          </div>
          <div>
            <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Services (séparés par virgule)</label>
            <Input placeholder="DJ, Animation, Sonorisation..." value={form.services}
              onChange={e => setForm(prev => ({ ...prev, services: e.target.value }))}
              style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={submit} size="sm" style={{ background: 'rgba(125,177,255,0.2)', color: 'var(--admin-blue)' }}>
              <Save size={13} className="mr-1" /> Sauvegarder
            </Button>
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : partenaires.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--admin-faint)' }}>
          <Link2 size={40} className="mx-auto mb-3 opacity-20" />
          <p>Aucun prestataire enregistré</p>
          <p className="text-xs mt-1">Ajoutez vos partenaires de confiance.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {partenaires.map(p => (
            <div key={p.id} className="p-4 rounded-2xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${p.actif !== false ? 'rgba(125,177,255,0.15)' : 'rgba(255,255,255,0.06)'}`, opacity: p.actif !== false ? 1 : 0.5 }}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                  {p.categorie && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(125,177,255,0.12)', color: 'var(--admin-blue)' }}>{p.categorie}</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(p)} className="p-1.5 rounded-lg" style={{ color: 'var(--admin-muted)' }}><Edit2 size={13} /></button>
                  <button onClick={() => toggleActif(p.id, p.actif)} className="p-1.5 rounded-lg text-xs"
                    style={{ color: p.actif !== false ? '#1F9D7A' : '#e53e3e' }}>
                    {p.actif !== false ? '●' : '○'}
                  </button>
                </div>
              </div>
              {p.description && <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--admin-muted)' }}>{p.description}</p>}
              <div className="space-y-1">
                {p.telephone && <p className="text-xs flex items-center gap-1" style={{ color: 'var(--admin-muted)' }}><Phone size={10}/>{p.telephone}</p>}
                {p.email && <p className="text-xs flex items-center gap-1" style={{ color: 'var(--admin-muted)' }}><Mail size={10}/>{p.email}</p>}
                {p.zone && <p className="text-xs flex items-center gap-1" style={{ color: 'var(--admin-muted)' }}><MapPin size={10}/>{p.zone}</p>}
                {p.site_web && <a href={p.site_web} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1" style={{ color: 'var(--admin-blue)' }}><ExternalLink size={10}/>Site web</a>}
              </div>
              {Array.isArray(p.services) && p.services.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {p.services.slice(0,4).map((s, i) => <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--admin-muted)' }}>{s}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
